import React, {useEffect, useMemo, useState} from 'react';
import {Button, Card, Input, message, Modal, Popconfirm, Space, Table, Tag} from 'antd';
import {DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, UserOutlined} from '@ant-design/icons';
import userApi from '../api/userApi';
import UserCreateEditModal from './UserCreateEditModal';
import ManagementHeader from './ManagementHeader';
import PermissionButton from './common/PermissionButton';

const REQ_CREATE = { method: 'POST', url: '/v1.0/user' };
const REQ_UPDATE = { method: 'PATCH', url: '/v1.0/user/{userId}' };
const REQ_DELETE = { method: 'DELETE', url: '/v1.0/user/{userId}' };

const UserManagement = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [current, setCurrent] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loginFilter, setLoginFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await userApi.listUsers();
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setData(list);
    } catch (e) {
      // 에러 토스트는 공통 인터셉터에서 처리
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => { setModalMode('create'); setCurrent(null); setModalOpen(true); };
  const openEdit = (record) => { setModalMode('edit'); setCurrent(record); setModalOpen(true); };

  const handleDelete = async (record) => {
    try { await userApi.deleteUser(record.userId); message.success('삭제되었습니다'); fetchData(); }
    catch { /* 에러 토스트는 공통 인터셉터에서 처리 */ }
  };

  const handleBatchDelete = () => {
    if (!selectedKeys.length) { message.warning('삭제할 사용자 선택'); return; }
    Modal.confirm({
      title: '선택 삭제',
      content: `${selectedKeys.length}명 사용자를 삭제하시겠습니까? (개별 API 순차 호출)`,
      okText: '삭제', okType: 'danger', cancelText: '취소',
      onOk: async () => {
        try {
          for (const id of selectedKeys) { await userApi.deleteUser(id); }
          message.success('삭제 완료');
          setSelectedKeys([]);
          fetchData();
        } catch { /* 에러 토스트는 공통 인터셉터에서 처리 */ }
      }
    });
  };

  const columns = [
    { title: '로그인ID', dataIndex: 'lgnUserNm', key: 'lgnUserNm', width: 160, sorter: (a, b) => (a.lgnUserNm || '').localeCompare(b.lgnUserNm || '') },
    { title: '사용자명', dataIndex: 'userNm', key: 'userNm', width: 160, sorter: (a, b) => (a.userNm || '').localeCompare(b.userNm || '') },
    { title: '프로필URL', dataIndex: 'profileUrl', key: 'profileUrl', width: 220, ellipsis: true, sorter: (a, b) => (a.profileUrl || '').localeCompare(b.profileUrl || '') },
    { title: '이메일', dataIndex: 'eml', key: 'eml', sorter: (a, b) => (a.eml || '').localeCompare(b.eml || '') },
    { title: '로그인', dataIndex: 'lgnYn', key: 'lgnYn', width: 80, sorter: (a, b) => (a.lgnYn || '').localeCompare(b.lgnYn || ''), render: v => <Tag color={v==='Y'?'green':'default'}>{v==='Y'?'Y':'N'}</Tag> },
    { title: '작업', key: 'actions', width: 170, render: (_, r) => (
      <Space>
        <PermissionButton size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} require={REQ_UPDATE}>수정</PermissionButton>
        <Popconfirm title="삭제" okText="삭제" cancelText="취소" onConfirm={() => handleDelete(r)}>
          <PermissionButton size="small" icon={<DeleteOutlined />} danger require={REQ_DELETE}>삭제</PermissionButton>
        </Popconfirm>
      </Space>
    ) }
  ];

  const filteredData = useMemo(() => {
    const lf = loginFilter.trim().toLowerCase();
    const ef = emailFilter.trim().toLowerCase();
    if (!lf && !ef) return data;
    return data.filter(u => (
      (!lf || (u.lgnUserNm || '').toLowerCase().includes(lf)) &&
      (!ef || (u.eml || '').toLowerCase().includes(ef))
    ));
  }, [data, loginFilter, emailFilter]);

  const headerActions = (
    <>
      <PermissionButton type="primary" icon={<PlusOutlined />} onClick={openCreate} require={REQ_CREATE}>사용자 생성</PermissionButton>
      <PermissionButton danger icon={<DeleteOutlined />} disabled={!selectedKeys.length} onClick={handleBatchDelete} require={REQ_DELETE}>선택삭제</PermissionButton>
      <Button icon={<ReloadOutlined />} onClick={fetchData}>새로고침</Button>
      <Input placeholder="로그인ID 검색" allowClear size="middle" style={{ width: 160 }} value={loginFilter} onChange={e => setLoginFilter(e.target.value)} />
      <Input placeholder="이메일 검색" allowClear size="middle" style={{ width: 200 }} value={emailFilter} onChange={e => setEmailFilter(e.target.value)} />
      <Button onClick={() => { setLoginFilter(''); setEmailFilter(''); }} disabled={!loginFilter && !emailFilter}>필터초기화</Button>
    </>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <ManagementHeader
        icon={<UserOutlined />}
        title="사용자 관리"
        actions={headerActions}
        description="시스템 사용자 계정을 생성/수정/삭제할 수 있습니다."
      />
      <Card size="small" styles={{ body: { padding: 0, overflowX: 'auto' } }}>
        <Table
          size="small"
          rowKey="userId"
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowSelection={{ selectedRowKeys: selectedKeys, onChange:setSelectedKeys }}
          pagination={{ pageSize: 15, showSizeChanger:false }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
      <UserCreateEditModal
        open={modalOpen}
        mode={modalMode}
        record={current}
        onCancel={() => setModalOpen(false)}
        onSuccess={() => { setModalOpen(false); fetchData(); }}
      />
    </div>
  );
};

export default UserManagement;
